import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BaseView, BaseSafeFormView, BaseText } from '@/components/base'
import {
  StickyHeaderNavigation,
  SearchInput,
  ContactsList,
  FavoritesList,
  AppButton,
} from '@/components'
import { ContactItem } from '@/components/ContactItem'
import { useSplitTransactionStore } from '@/store/useSplitTransactionStore'
import type { UsersAndContacts } from '@/types/contacts'
import { useScrollToTop } from '@/hooks'
import { useTranslation } from 'react-i18next'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'
import { useFavoriteUsers } from '@/hooks/useContacts'

const { formatCryptoAmount, formatFiatCurrency } = currencyUtils

export const SplitRequestSelectUsersScreen: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offsets = useOffsets()

  const transaction = useSplitTransactionStore((s) => s.transaction)
  const { data: favorites = [], isLoading: isLoadingFavorites } =
    useFavoriteUsers()
  const selectedRecipients = useSplitTransactionStore(
    (s) => s.selectedRecipients,
  )
  const toggleRecipient = useSplitTransactionStore((s) => s.toggleRecipient)
  const isRecipientSelected = useSplitTransactionStore(
    (s) => s.isRecipientSelected,
  )
  const clearSplit = useSplitTransactionStore((s) => s.clearSplit)
  const canAddMore = useSplitTransactionStore((s) => s.canAddMore)
  const maxRecipients = useSplitTransactionStore((s) => s.maxRecipients)
  const getAmountPerPerson = useSplitTransactionStore(
    (s) => s.getAmountPerPerson,
  )
  const getInitiatorAmount = useSplitTransactionStore(
    (s) => s.getInitiatorAmount,
  )

  // Local state for search
  const [searchValue, setSearchValue] = useState('')
  const [immediateSearchValue, setImmediateSearchValue] = useState('')

  // Track contacts selected from search (not in favorites) to keep showing them
  const [selectedFromSearch, setSelectedFromSearch] = useState<
    UsersAndContacts[]
  >([])

  // Scroll functionality
  const { handleScroll, scrollElementRef } = useScrollToTop(0, {
    scrollThreshold: window.innerHeight / 2,
    minItemsCount: 8,
    throttleDelay: 200,
  })

  // Redirect if no transaction
  useEffect(() => {
    if (!transaction) {
      navigate('/dashboard', { replace: true })
    }
  }, [transaction, navigate])

  // Restore selectedFromSearch when navigating back from confirm screen
  // This ensures previously selected contacts remain visible
  useEffect(() => {
    if (selectedRecipients.length > 0 && selectedFromSearch.length === 0) {
      // Map SelectedRecipient to UsersAndContacts format
      const mappedContacts: UsersAndContacts[] = selectedRecipients.map(
        (r) => ({
          id: r.id,
          username: r.username,
          first_name: r.name.split(' ')[0] || r.name,
          last_name: r.name.split(' ').slice(1).join(' ') || '',
          phone_number: r.phoneNumber || '',
          avatar_path: r.avatar,
          avatarBg: r.avatarBg,
        }),
      )
      setSelectedFromSearch(mappedContacts)
    }
  }, []) // Only run on mount

  const handleContactSelected = useCallback(
    (contact: UsersAndContacts) => {
      if (isRecipientSelected(contact.id)) {
        toggleRecipient(contact)
        // Remove from selectedFromSearch when deselected
        setSelectedFromSearch((prev) => prev.filter((c) => c.id !== contact.id))
      } else if (canAddMore()) {
        toggleRecipient(contact)
        // Add to selectedFromSearch for ALL selections (from favorites or search)
        // This ensures all selected contacts are visible in the "Selected" section
        if (!selectedFromSearch.some((c) => c.id === contact.id)) {
          setSelectedFromSearch((prev) => [...prev, contact])
        }
      }
    },
    [toggleRecipient, isRecipientSelected, canAddMore, selectedFromSearch],
  )

  const handleBackPress = () => {
    clearSplit()
    navigate(-1)
  }

  const handleImmediateSearchChange = useCallback((text: string) => {
    setImmediateSearchValue(text)
  }, [])

  const handleSearchChange = useCallback((text: string) => {
    setSearchValue(text)
  }, [])

  const handleClear = useCallback(() => {
    setImmediateSearchValue('')
    setSearchValue('')
    // Don't clear selectedFromSearch - we want to keep showing selected contacts
  }, [])

  const handleConfirmSelection = useCallback(() => {
    if (selectedRecipients.length === 0) return
    navigate('/split-request/confirm')
  }, [selectedRecipients.length, navigate])

  // Attach scroll handler
  useEffect(() => {
    const scrollElement = scrollElementRef.current
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll, scrollElementRef])

  if (!transaction) {
    return null
  }

  const amountPerPerson = getAmountPerPerson()
  const initiatorAmount = getInitiatorAmount()

  return (
    <BaseSafeFormView
      style={{}}
      px={0}
      pb={0}
      flex={1}
      bgColor="background.screen"
    >
      <StickyHeaderNavigation
        title={t('translation:splitRequest', { defaultValue: 'Split request' })}
        onBackPress={handleBackPress}
      />

      <div
        ref={scrollElementRef as React.RefObject<HTMLDivElement>}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '28px',
          padding: 0,
          paddingLeft: '13px',
          paddingRight: '13px',
          flex: 1,
          overflowY: 'auto',
          height: '100vh',
        }}
      >
        {/* Transaction Info Card */}
        <BaseView
          bgColor="background.cardItemRoot"
          style={{
            padding: '20px',
            borderRadius: '16px',
            marginTop: '16px',
          }}
        >
          <BaseText
            fontSize={14}
            fontFamily="medium"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: '8px' }}
          >
            {t('translation:originalAmount', {
              defaultValue: 'Original amount',
            })}
          </BaseText>
          <BaseText
            fontSize={28}
            fontFamily="bold"
            color="label.default"
            textAlign="center"
            style={{ marginBottom: '4px' }}
          >
            {transaction.amount.amount} {transaction.amount.currency}
          </BaseText>
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: '16px' }}
          >
            ≈
            {formatFiatCurrency({
              fiatCode: transaction.amount.fiat_currency,
              value: Number(transaction.amount.fiat_amount),
              showDecimals: true,
            })}
          </BaseText>

          {/* Split Preview */}
          {
            <>
              <BaseText
                fontSize={14}
                fontFamily="medium"
                color="label.description"
                textAlign="left"
                style={{ marginBottom: '12px' }}
              >
                {t('translation:splitPreview', {
                  defaultValue: 'Split preview:',
                })}
              </BaseText>

              <BaseView gap={8}>
                <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {t('translation:yourShare', { defaultValue: 'Your share' })}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {formatCryptoAmount({
                      value: initiatorAmount,
                      cryptoCode: transaction.amount.currency,
                      showDecimals: true,
                      showCryptoCode: false,
                    })}{' '}
                    {transaction.amount.currency}
                  </BaseText>
                </BaseView>

                <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {selectedRecipients.length > 0
                      ? selectedRecipients.length > 1 && selectedRecipients[0]
                        ? t('translation:perPerson', {
                            defaultValue: 'Each person pays',
                          })
                        : `${selectedRecipients[0].name} ${t(
                            'translation:personPays',
                            {
                              defaultValue: 'pays',
                            },
                          )}`
                      : '...'}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {formatCryptoAmount({
                      value: amountPerPerson,
                      cryptoCode: transaction.amount.currency,
                      showDecimals: true,
                      showCryptoCode: false,
                    })}{' '}
                    {transaction.amount.currency}
                  </BaseText>
                </BaseView>

                {/* <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {t('translation:totalPeople', {
                      defaultValue: 'Selected',
                    })}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {selectedRecipients.length > 0 ? totalPeople - 1 : '...'}
                  </BaseText>
                </BaseView> */}
              </BaseView>
            </>
          }
        </BaseView>

        {/* Search */}
        <BaseView gap={20}>
          <SearchInput
            placeholder={t('form:placeholders.search')}
            withoutBorder
            defaultValue={immediateSearchValue}
            onChangeText={handleImmediateSearchChange}
            onChangeTextDebounced={handleSearchChange}
            clean={handleClear}
            debounceDelay={300}
          />
        </BaseView>

        <BaseView
          flex={1}
          style={{
            paddingBottom: selectedRecipients.length > 0 ? '140px' : '20px',
          }}
        >
          {/* Favorites List */}
          <FavoritesList
            onContactSelected={handleContactSelected}
            className=""
            isMultiSelectMode={true}
            isContactSelected={isRecipientSelected}
            isLoading={isLoadingFavorites}
            favorites={favorites}
          />

          {/* Contacts Section - show during search */}
          {immediateSearchValue && (
            <BaseView mt={16}>
              <ContactsList
                search={searchValue}
                onContactSelected={handleContactSelected}
                isEnabledTransferToNonUsers={true}
                isMultiSelectMode={true}
                isContactSelected={isRecipientSelected}
              />
            </BaseView>
          )}

          {/* Selected contacts - show when not searching */}
          {!immediateSearchValue && selectedFromSearch.length > 0 && (
            <BaseView mt={16}>
              <BaseText
                fontSize={18}
                fontFamily="semibold"
                color="label.textTextInput"
                style={{ marginBottom: '12px' }}
              >
                {t('translation:selected', { defaultValue: 'Selected' })} (
                {
                  selectedFromSearch.filter((contact) =>
                    isRecipientSelected(contact.id),
                  ).length
                }
                )
              </BaseText>
              <BaseView gap={8}>
                {selectedFromSearch
                  .filter((contact) => isRecipientSelected(contact.id))
                  .map((contact, index) => (
                    <ContactItem
                      key={`selected-${contact.id}-${index}`}
                      contact={contact}
                      onPress={handleContactSelected}
                      isMultiSelectMode={true}
                      isSelected={isRecipientSelected(contact.id)}
                    />
                  ))}
              </BaseView>
            </BaseView>
          )}

          {/* Selection info */}
          {selectedRecipients.length > 9 && (
            <BaseView mt={16} px={0}>
              <BaseText
                fontSize={14}
                fontFamily="regular"
                color="label.description"
                textAlign="center"
              >
                {selectedRecipients.length} of {maxRecipients} people selected
              </BaseText>
            </BaseView>
          )}
        </BaseView>
      </div>

      {/* Scroll to Top Button */}

      {/* Fixed Bottom Confirm Button */}
      {selectedRecipients.length > 0 && (
        <BaseView
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px',
            paddingBottom: `${offsets.bottom + 16}px`,
            backgroundColor: '#131415',
            zIndex: 100,
          }}
        >
          <AppButton
            label={t('translation:continue', {
              defaultValue: 'Continue',
            })}
            onPress={handleConfirmSelection}
          />
        </BaseView>
      )}
    </BaseSafeFormView>
  )
}

export default SplitRequestSelectUsersScreen
